// @ts-check
// Lista das instituições do painel de suporte: busca por nome ou endereço, filtro por situação da assinatura e por
// plano, colunas de plano, situação, usuários, criação e último backup; cada linha abre a ficha da instituição.
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Link, searchParamsState, updateSearchParams } from '../../../app/router.js';
import { fetchAdminTenants } from '../../../data/operations.js';
import { formatDate, formatDateTime } from '../../../lib/format.js';
import { adminInstitutionPath, matchesInstitutionQuery } from '../../../lib/admin-navigation.js';
import { showOperationsError } from '../operations-format.js';
import { AdminEmpty, AdminLoading, AdminPage, FilterField, FilterSelect, SubscriptionStatusTag, formatInteger } from './admin-ui.js';
import { Icon } from '../../../ui/icon.js';
import { t } from '../../../i18n/index.js';

const STATUS_FILTERS = ['ALL', 'ACTIVE', 'TRIALING', 'PAST_DUE', 'SUSPENDED', 'CANCELED', 'LEGACY'];

export function AdminInstitutionsPage() {
  const [tenants, setTenants] = useState(/** @type {any[] | null} */ (null));
  const [query, setQuery] = useState('');
  const parameters = searchParamsState.value;
  const statusFilter = STATUS_FILTERS.includes(parameters.get('status') ?? '') ? /** @type {string} */ (parameters.get('status')) : 'ALL';
  const planFilter = parameters.get('plan') ?? 'ALL';

  useEffect(() => {
    fetchAdminTenants().then(setTenants).catch(showOperationsError);
  }, []);

  // filtro pelo plano: a chave é o valor, o nome é o rótulo (planos antigos continuam aparecendo pelo nome)
  const plans = useMemo(() => {
    /** @type {Map<string, string>} */
    const names = new Map();
    for (const tenant of tenants ?? []) if (tenant.planKey) names.set(tenant.planKey, tenant.planName ?? tenant.planKey);
    return [...names.entries()].sort((first, second) => first[1].localeCompare(second[1], 'pt-BR'));
  }, [tenants]);
  const visible = (tenants ?? []).filter((tenant) => matchesInstitutionQuery(tenant, query)
    && (statusFilter === 'ALL' || tenant.subscriptionState?.status === statusFilter)
    && (planFilter === 'ALL' || tenant.planKey === planFilter));
  /** @param {string} key @param {Record<string, unknown>} [values] */
  const text = (key, values) => t(`operations.admin.panel.institutions.${key}`, values);

  return html`<${AdminPage} icon="IconBuilding" title=${t('operations.admin.panel.sections.institutions')}
    subtitle=${tenants ? text('count', { visible: visible.length, total: tenants.length }) : null} wide=${true}>
    <div class="admin-toolbar">
      <label class="admin-search-field">
        <${Icon} name="IconSearch" size=${14} />
        <input class="admin-search-input" type="search" value=${query} placeholder=${t('operations.admin.search')} aria-label=${t('operations.admin.search')}
          onInput=${(/** @type {Event} */ event) => setQuery(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      <${FilterField} label=${text('statusFilter')}>
        <${FilterSelect} value=${statusFilter} ariaLabel=${text('statusFilter')} onChange=${(/** @type {string} */ value) => updateSearchParams({ status: value === 'ALL' ? null : value })}
          options=${STATUS_FILTERS.map((status) => ({ value: status, label: status === 'ALL' ? text('allStatuses') : t(`operations.billing.statuses.${status}`) }))} />
      </${FilterField}>
      <${FilterField} label=${text('planFilter')}>
        <${FilterSelect} value=${planFilter} ariaLabel=${text('planFilter')} onChange=${(/** @type {string} */ value) => updateSearchParams({ plan: value === 'ALL' ? null : value })}
          options=${[{ value: 'ALL', label: text('allPlans') }, ...plans.map(([key, name]) => ({ value: key, label: name }))]} />
      </${FilterField}>
    </div>
    ${tenants === null ? html`<${AdminLoading} rows=${5} />` : visible.length === 0
      ? html`<${AdminEmpty} icon="IconBuilding" title=${text('empty')} />`
      : html`<div class="settings-table admin-table">
          <div class="settings-table-row settings-table-header admin-institution-row">
            <span>${t('operations.admin.columns.institution')}</span><span>${text('plan')}</span><span>${t('operations.admin.columns.subscription')}</span>
            <span class="settings-table-number">${t('operations.admin.columns.members')}</span><span>${t('operations.admin.columns.createdAt')}</span><span>${t('operations.admin.columns.lastBackup')}</span><span></span>
          </div>
          ${visible.map((tenant) => html`<${Link} key=${tenant.id} href=${adminInstitutionPath(tenant.id)} className="settings-table-row admin-institution-row admin-row-link">
            <span class="settings-table-name admin-cell-stack"><span>${tenant.displayName}</span><span class="settings-table-muted">${tenant.slug}</span></span>
            <span class="settings-table-muted">${tenant.planName ?? tenant.planKey ?? '-'}</span>
            <span><${SubscriptionStatusTag} status=${tenant.subscriptionState?.status} /></span>
            <span class="settings-table-number">${formatInteger(tenant.memberCount)}</span>
            <span class="settings-table-muted">${formatDate(tenant.createdAt)}</span>
            <span class="settings-table-muted">${tenant.lastBackupAt ? formatDateTime(tenant.lastBackupAt) : t('operations.admin.noBackup')}</span>
            <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${14} /></span>
          </${Link}>`)}
        </div>`}
  </${AdminPage}>`;
}
